// Para onde ir depois de entrar. Quem abre uma página protegida sem sessão (ex.: o botão de um
// e-mail) é mandado para o /login pelo PrivateRoute, com a página pedida em `state.from` — e é
// para lá que deve voltar, não para a raiz (que o largava na lista de propriedades).
//
// O login com Google sai da SPA (redirect para o Google e de volta) e perde esse state, por isso
// a página fica também no sessionStorage antes de sair: o backend volta para "/" (sucesso) ou para
// o /login (escolher a empresa), e é daí que se retoma. Expira, para um login abandonado a meio
// não mandar para uma página antiga o próximo login no mesmo separador.
const STORAGE_KEY = 'login:destino'
const VALIDADE_MS = 15 * 60 * 1000

// Não são destinos: voltariam ao login, ao registo, ou à raiz (que já é o comportamento normal).
const ROTAS_PUBLICAS = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/confirm-email']

/** Só caminhos internos da app — nunca um URL absoluto nem "//outro-site" (open redirect). */
function isValidDestination(path: unknown): path is string {
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return false
    const pathname = path.split(/[?#]/)[0]
    return !ROTAS_PUBLICAS.includes(pathname)
}

function readStored(): string | null {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown }
        if (typeof at !== 'number' || Date.now() - at > VALIDADE_MS) return null
        return isValidDestination(path) ? path : null
    } catch {
        return null
    }
}

/**
 * A página a abrir depois do login: a de onde o PrivateRoute mandou para o /login (`state.from`)
 * ou, na volta do Google, a guardada antes de sair. `null` = nenhuma (segue para a raiz).
 */
export function getLoginDestination(state: unknown): string | null {
    const from = state && typeof state === 'object' ? (state as { from?: unknown }).from : null
    if (from && typeof from === 'object') {
        const { pathname = '', search = '', hash = '' } = from as { pathname?: string; search?: string; hash?: string }
        const path = `${pathname}${search}${hash}`
        if (isValidDestination(path)) return path
    }
    return readStored()
}

/** Guarda a página antes de sair da SPA para o login com Google. */
export function rememberLoginDestination(path: string | null) {
    try {
        if (isValidDestination(path)) {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ path, at: Date.now() }))
        } else {
            window.sessionStorage.removeItem(STORAGE_KEY)
        }
    } catch {
        // Sem storage: o login com Google cai na raiz, como antes.
    }
}

/** A página guardada antes do login com Google, se ainda valer. */
export function readRememberedDestination(): string | null {
    return readStored()
}

export function clearRememberedDestination() {
    try {
        window.sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        // nada a limpar
    }
}
